import React, { useEffect, useContext } from 'react'
import { Link } from 'react-router-dom'
import { Button, Box } from '@deal/components'
import ContentfulQuery from '#src/containers/Contentful'
import { EngagementChannelCategoryFragment } from '#src/generated/graphql'
import { AnalyticsContext } from '#src/containers/Analytics'
import { generateCreateUrlFromCategory } from '#src/services/path'
import desktopStyles from './styles-desktop.css'
import mobileStyles from './styles-mobile.css'
import BackgroundImage from '#src/components/BackgroundImage'

interface FixedBarProps {
  category: EngagementChannelCategoryFragment
}

interface FixedBarHeaderProps {
  slug: string | null
}

interface FixedBarActionProps {
  className?: string
  onClick: () => void
  pathCategorySlug: string | null
}

const FixedBarAction: React.SFC<FixedBarActionProps> = ({
  onClick,
  className,
  pathCategorySlug
}) => {
  return (
    <Link
      to={generateCreateUrlFromCategory({
        maybeCategorySlug: pathCategorySlug,
        sourceKey: 'fixed-bar-engagement-channel',
        pathIntent: 'request'
      })}
    >
      <Button size="large" onClick={onClick} className={className}>
        Get Started
      </Button>
    </Link>
  )
}

const FixedBarHeader: React.SFC<FixedBarHeaderProps> = ({ slug }) => {
  const defaultHeader = 'Talk to an expert to get the best recommendations for you'
  const analytics = useContext(AnalyticsContext)

  useEffect(() => {
    analytics?.events.engagementChannelViewed({
      engagement_channel: 'fixed-bar',
      variation: slug || '',
      category: slug || ''
    })
  }, [])

  if (!slug) {
    return <>{defaultHeader}</>
  }

  return (
    <ContentfulQuery template="productFixedBar" variation={slug}>
      {({ content, loading }) => {
        if (loading) {
          return null
        } else if (content) {
          return <>{content.header}</>
        } else {
          return <>{defaultHeader}</>
        }
      }}
    </ContentfulQuery>
  )
}

const FixedBar: React.SFC<FixedBarProps> = ({ category }) => {
  const fixedBarDesktop = category.contentAssets.fixedBarDesktop
  const callout = category.contentAssets.callout
  const pathCategorySlug = category.ancestorWithPath && category.ancestorWithPath.slug
  const analytics = useContext(AnalyticsContext)

  return (
    <>
      <div
        className={desktopStyles.wrapper}
        style={{
          backgroundImage: fixedBarDesktop
            ? `url(${fixedBarDesktop.imageUrl}?auto=compress%2Cformat)`
            : undefined
        }}
      >
        <div className={desktopStyles.backgroundMask} />
        <Box items="center" justify="end" className={desktopStyles.bar}>
          <div className={desktopStyles.label}>
            <FixedBarHeader slug={category.slug} />
          </div>
          <div className={desktopStyles.button}>
            <FixedBarAction
              pathCategorySlug={pathCategorySlug}
              onClick={() => {
                analytics?.events.engagementChannelSubmitted({
                  engagement_channel: 'fixed-bar',
                  variation: category.slug,
                  category: category.slug
                })
              }}
            />
          </div>
        </Box>
      </div>
      <div className={mobileStyles.wrapper}>
        <BackgroundImage
          containerClassName={mobileStyles.image}
          contentClassName={mobileStyles.content}
          src={callout && callout.imageUrl ? callout.imageUrl : ''}
          size="100vw"
        >
          <div className={mobileStyles.backgroundMask} />
          <div className={mobileStyles.headline}>
            <FixedBarHeader slug={category.slug} />
          </div>
        </BackgroundImage>
        <FixedBarAction
          pathCategorySlug={pathCategorySlug}
          className={mobileStyles.button}
          onClick={() => {
            analytics?.events.mobileFixedBarClicked()
          }}
        />
      </div>
    </>
  )
}

export default FixedBar
